import { useCallback, useEffect } from "react";

import { clearSessionStorage, getSessionStorageValue, updateSessionStorage } from "@/hooks/useLocalStorage";
import { useQueryParams } from "@/hooks/useQueryParam";

import type { PortfolioRouteParams } from "../../routes";

const PORTFOLIO_OVERVIEW_FILTER_STORAGE_KEY = "portfolio-overview-filters";

type PortfolioOverviewQueryParams = PortfolioRouteParams["overview"];
type PersistedPortfolioOverviewFilters = Pick<PortfolioOverviewQueryParams, "startDate">;

/**
 * Keeps Portfolio Overview project filters in the URL. The page-owned reporting period is persisted in session storage
 * so it survives navigation, while the shared project scope stays URL-only.
 */
export function usePortfolioOverviewQueryParams(): readonly [
  Partial<PortfolioOverviewQueryParams>,
  (newValue: PortfolioOverviewQueryParams) => void,
] {
  const [queryParams, setQueryParams] = useQueryParams<PortfolioOverviewQueryParams>();

  // Seed the page-owned reporting period from session storage when the URL has none.
  useEffect(() => {
    if (queryParams.startDate) {
      return;
    }

    const stored = getSessionStorageValue<PersistedPortfolioOverviewFilters>(PORTFOLIO_OVERVIEW_FILTER_STORAGE_KEY, {});
    if (stored.startDate) {
      setQueryParams({ startDate: stored.startDate }, { replace: true });
    }
    // Restore the date once when entering Portfolio Overview. Later changes go through updateQueryParams.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateQueryParams = useCallback(
    (newValue: PortfolioOverviewQueryParams) => {
      setQueryParams(newValue);

      // Only touch the stored reporting period when this update addresses it; partial updates
      // (such as changing the shared project scope) must leave the remembered date intact.
      if (!("startDate" in newValue)) {
        return;
      }

      if (newValue.startDate) {
        updateSessionStorage<PersistedPortfolioOverviewFilters>(PORTFOLIO_OVERVIEW_FILTER_STORAGE_KEY, {
          startDate: newValue.startDate,
        });
      } else {
        clearSessionStorage(PORTFOLIO_OVERVIEW_FILTER_STORAGE_KEY);
      }
    },
    [setQueryParams],
  );

  return [queryParams, updateQueryParams] as const;
}
